import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { SlidersHorizontal, X } from 'lucide-react';
import { useLocale } from '../context/LocaleContext';
import { fetchProducts } from '../api/client';
import { useTaxonomies } from '../context/TaxonomiesContext';

const SORT_KEYS = ['popular', 'new', 'plh', 'phl', 'disc'];
const SORT_TR = { popular: 'sortPopular', new: 'sortNew', plh: 'sortPlh', phl: 'sortPhl', disc: 'sortDisc' };
const VALID_GENDERS = ['men', 'women'];
const PRICE_MIN = 500;
const PRICE_MAX = 5000;
const FALLBACK_TYPES = ['Oversized T-Shirts', 'Hoodies', 'Sweatshirts', 'Joggers', 'Shirts', 'Backpacks', 'Caps', 'Sneakers'];

const buildTitle = ({ q, category, gender, t }) => {
  if (q) return `${t('searchResults')}: "${q}"`;
  if (category) return category.toUpperCase();
  if (gender === 'men') return t('mensCollection');
  if (gender === 'women') return t('womensCollection');
  return t('shopAll');
};

const ShopListing = () => {
  const params = useParams();
  const [sp] = useSearchParams();
  const q = sp.get('q');
  const { t, formatPrice } = useLocale();
  const { types } = useTaxonomies();
  const [sort, setSort] = useState('popular');
  const [priceMax, setPriceMax] = useState(PRICE_MAX);
  const [minDiscount, setMinDiscount] = useState(0);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const ALL_TYPES = types.length > 0 ? types.map((t) => t.label) : FALLBACK_TYPES;

  const genderFilter = params.gender && VALID_GENDERS.includes(params.gender) ? params.gender : null;
  const categoryFilter = params.category ? params.category.replace(/-/g, ' ') : null;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const apiParams = {};
        if (genderFilter) apiParams.gender = genderFilter;
        if (categoryFilter) apiParams.category = categoryFilter.replace(/ /g, '-');
        if (q) apiParams.q = q;
        if (sort && sort !== 'popular') apiParams.sort = sort;
        apiParams.price_max = priceMax;
        apiParams.min_discount = minDiscount;
        apiParams.limit = 200;
        const data = await fetchProducts(apiParams);
        let items = data.items || [];
        // Local filter for multi-type selection (backend supports single category)
        if (selectedTypes.length > 0) {
          items = items.filter((p) => selectedTypes.includes(p.type));
        }
        setProducts(items);
      } catch (err) { console.warn('[Shop] fetch failed:', err.message); }
      finally { setLoading(false); }
    })();
  }, [genderFilter, categoryFilter, q, sort, priceMax, minDiscount, selectedTypes]);

  const toggleType = (ty) => setSelectedTypes((prev) => (prev.includes(ty) ? prev.filter((x) => x !== ty) : [...prev, ty]));
  const clearAllFilters = () => { setSelectedTypes([]); setMinDiscount(0); setPriceMax(PRICE_MAX); };
  const hasActiveFilters = selectedTypes.length > 0 || minDiscount > 0 || priceMax < PRICE_MAX;
  const title = buildTitle({ q, category: categoryFilter, gender: genderFilter, t });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="bg-[#f5f5f5] py-6">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h1 className="text-2xl md:text-3xl font-black">{title}</h1>
          <p className="text-sm text-gray-600 mt-1">{products.length} {t('products')}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex gap-6">
        <aside className="hidden md:block w-60 shrink-0">
          <div className="sticky top-24">
            <div className="flex items-center gap-2 pb-3 border-b">
              <SlidersHorizontal size={16} />
              <h3 className="font-bold text-sm">{t('filters')}</h3>
            </div>
            <div className="py-4 border-b">
              <h4 className="font-semibold text-xs tracking-wider mb-3">{t('category')}</h4>
              <div className="space-y-2">
                {ALL_TYPES.map((ty) => (
                  <label key={ty} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={selectedTypes.includes(ty)} onChange={() => toggleType(ty)} className="accent-[#22c55e]" />
                    <span>{ty}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="py-4 border-b">
              <h4 className="font-semibold text-xs tracking-wider mb-3">{t('price')} ({t('upTo')} {formatPrice(priceMax)})</h4>
              <input type="range" min={PRICE_MIN} max={PRICE_MAX} step="100" value={priceMax} onChange={(e) => setPriceMax(+e.target.value)} className="w-full accent-[#22c55e]" />
            </div>
            <div className="py-4 border-b">
              <h4 className="font-semibold text-xs tracking-wider mb-3">{t('discount')} ({t('min')} {minDiscount}%)</h4>
              <input type="range" min="0" max="70" step="5" value={minDiscount} onChange={(e) => setMinDiscount(+e.target.value)} className="w-full accent-[#22c55e]" />
            </div>
            {hasActiveFilters && (
              <button onClick={clearAllFilters} className="mt-4 text-xs font-bold text-[#22c55e] flex items-center gap-1">
                <X size={12} /> {t('clearAll')}
              </button>
            )}
          </div>
        </aside>

        <main className="flex-1">
          <div className="flex justify-end mb-4">
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="border rounded px-3 py-2 text-sm outline-none">
              {SORT_KEYS.map((s) => <option key={s} value={s}>{`${t('sortLabel')}: ${t(SORT_TR[s])}`}</option>)}
            </select>
          </div>
          {loading ? (
            <div className="text-center py-20 text-gray-500">Loading products...</div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 text-gray-500">{t('noProducts')}</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default ShopListing;
